'use client'
import Link from 'next/link'
import { useParams } from 'next/navigation'
import React, { useEffect, useState, useTransition } from 'react'

import type { Header } from '@/payload-types'

import { useLocale } from 'next-intl'
import localization from '@/i18n/localization'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { TypedLocale } from 'payload'
import { usePathname, useRouter } from '@/i18n/routing'
import { Media } from '@/components/Media'
import { Menu } from "lucide-react"
import { SideMenu } from './side-menu'
import { MenuBar } from './menu-bar'

interface HeaderClientProps {
  header: Header
}

export const HeaderClient: React.FC<HeaderClientProps> = ({ header }) => {

  const pathname = usePathname()
  const { logo , buttons , navLinks , scrolledLogo , sociallinks  } = header
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0)
    }
    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  const menuItems = (navLinks || []).map(navLink => {
    const mainLinkData = navLink.mainLink?.[0]?.link;
    const mainUrl = mainLinkData?.url || 
      (mainLinkData?.reference?.value && typeof mainLinkData.reference.value !== 'number' ? 
        `/${mainLinkData.reference.value.slug || ''}` : '#');

    return {
      id: navLink.id || `nav-${Math.random()}`,
      title: mainLinkData?.label || '',
      href: mainUrl,
      icon : mainLinkData?.fiIcon || '',
      expandable: (navLink.subLinks || []).length > 0,
      items: (navLink.subLinks || []).map(subLink => {
        const subLinkData = subLink.link;
        const subUrl = subLinkData?.url || 
          (subLinkData?.reference?.value && typeof subLinkData.reference.value !== 'number' ? 
            `/${subLinkData.reference.value.slug || ''}` : '#');

        return {
          id: subLink.id || `sub-${Math.random()}`,
          title: subLinkData?.label || '',
          href: subUrl
        };
      })
    };
  }).filter(item => item.title);

  return (
    <header
      data-theme='light'
      className={`px-8 md:px-12 lg:px-16 mx-auto bg-background text-primary z-20 ${isScrolled ? 'py-2 fixed top-0 left-0 right-0 opacity-95' : 'py-4'} flex justify-end items-center gap-2 transition-all duration-300 ${isScrolled ? 'shadow-md' : ''}`} >
      <Link href="/" className="me-auto">
        {isScrolled && scrolledLogo ? (
          <Media resource={scrolledLogo} imgClassName='h-12 !w-auto' />
        ) : (
          logo && <Media resource={logo} imgClassName={isScrolled ? 'h-12 !w-auto' : 'h-16 !w-auto'} />
        )}
      </Link>
      
      <MenuBar items={menuItems.map(item => ({
        icon: item.icon, // Replace with actual icon class names
        label: item.title,
        href: item.href,
        gradient: "radial-gradient(circle, rgba(59,130,246,0.15) 0%, rgba(37,99,235,0.06) 50%, rgba(29,78,216,0) 100%)", // Replace with actual gradients
        iconColor: "text-primary", // Replace with actual icon colors
      }))} />

      {/* <LocaleSwitcher /> */}
      <button
        onClick={() => setIsMenuOpen(true)}
        className="flex gap-2 items-center font-light text-xs"
        aria-label="Open menu"
      >
        <span>
القائمة
        </span>
        <Menu className="h-6 w-6" />
      </button>
      <SideMenu items={menuItems} sociallinks={sociallinks as any} isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} />
      {/* <HeaderNav header={header} /> */}
    </header>
  )
}

export function LocaleSwitcher() {
  // inspired by https://github.com/amannn/next-intl/blob/main/examples/example-app-router/src/components/LocaleSwitcherSelect.tsx
  const locale = useLocale()
  const router = useRouter()
  const [, startTransition] = useTransition()
  const pathname = usePathname()
  const params = useParams()

  function onSelectChange(value: TypedLocale) {
    startTransition(() => {
      router.replace(
        // @ts-expect-error -- TypeScript will validate that only known `params`
        // are used in combination with a given `pathname`. Since the two will
        // always match for the current route, we can skip runtime checks.
        { pathname, params },
        { locale: value },
      )
    })
  }

  return (
    <Select onValueChange={onSelectChange} value={locale}>
      <SelectTrigger className="w-auto text-sm bg-transparent gap-2 pl-0 md:pl-3 border-none">
        <SelectValue placeholder="Theme" />
      </SelectTrigger>
      <SelectContent>
        {localization.locales
          .sort((a, b) => a.label.localeCompare(b.label)) // Ordenar por label
          .map((locale) => (
            <SelectItem value={locale.code} key={locale.code}>
              {locale.label}
            </SelectItem>
          ))}
      </SelectContent>
    </Select>
  )
}
